import React, { useState, useRef, useEffect } from 'react';
import { CollectionsSectionProps } from '../types/components';
import { CollectionImage } from '../types/images';

// Sample collection data with real jewellery images from public folder
const collectionData: CollectionImage[] = [
  // Gold collection (12 images from public/Gold folder)
  { id: 'gold-1', url: '/Gold/1.jpg', alt: 'Gold jewelry 1', collection: 'gold' },
  { id: 'gold-2', url: '/Gold/2.jpg', alt: 'Gold jewelry 2', collection: 'gold' },
  { id: 'gold-3', url: '/Gold/3.jpg', alt: 'Gold jewelry 3', collection: 'gold' },
  { id: 'gold-4', url: '/Gold/4.jpg', alt: 'Gold jewelry 4', collection: 'gold' },
  { id: 'gold-5', url: '/Gold/5.jpg', alt: 'Gold jewelry 5', collection: 'gold' },
  { id: 'gold-6', url: '/Gold/6.jpg', alt: 'Gold jewelry 6', collection: 'gold' },
  { id: 'gold-7', url: '/Gold/7.jpg', alt: 'Gold jewelry 7', collection: 'gold' },
  { id: 'gold-8', url: '/Gold/8.jpg', alt: 'Gold jewelry 8', collection: 'gold' },
  { id: 'gold-9', url: '/Gold/9.jpg', alt: 'Gold jewelry 9', collection: 'gold' },
  { id: 'gold-10', url: '/Gold/10.jpg', alt: 'Gold jewelry 10', collection: 'gold' },
  { id: 'gold-11', url: '/Gold/11.jpg', alt: 'Gold jewelry 11', collection: 'gold' },
  { id: 'gold-12', url: '/Gold/12.jpg', alt: 'Gold jewelry 12', collection: 'gold' },
  
  // Silver collection (12 images from public/Silver folder)
  { id: 'silver-1', url: '/Silver/1.jpg', alt: 'Silver jewelry 1', collection: 'silver' },
  { id: 'silver-2', url: '/Silver/2.jpg', alt: 'Silver jewelry 2', collection: 'silver' },
  { id: 'silver-3', url: '/Silver/3.jpg', alt: 'Silver jewelry 3', collection: 'silver' },
  { id: 'silver-4', url: '/Silver/4.jpg', alt: 'Silver jewelry 4', collection: 'silver' },
  { id: 'silver-5', url: '/Silver/5.jpg', alt: 'Silver jewelry 5', collection: 'silver' },
  { id: 'silver-6', url: '/Silver/6.jpg', alt: 'Silver jewelry 6', collection: 'silver' },
  { id: 'silver-7', url: '/Silver/7.jpg', alt: 'Silver jewelry 7', collection: 'silver' },
  { id: 'silver-8', url: '/Silver/8.jpg', alt: 'Silver jewelry 8', collection: 'silver' },
  { id: 'silver-9', url: '/Silver/9.jpg', alt: 'Silver jewelry 9', collection: 'silver' },
  { id: 'silver-10', url: '/Silver/10.png', alt: 'Silver jewelry 10', collection: 'silver' },
  { id: 'silver-11', url: '/Silver/11.jpg', alt: 'Silver jewelry 11', collection: 'silver' },
  { id: 'silver-12', url: '/Silver/12.jpg', alt: 'Silver jewelry 12', collection: 'silver' },
  
  // Kundan collection (12 images from public/Kundan folder)
  { id: 'kundan-1', url: '/Kundan/1.jpg', alt: 'Kundan jewelry 1', collection: 'kundan' },
  { id: 'kundan-2', url: '/Kundan/2.jpg', alt: 'Kundan jewelry 2', collection: 'kundan' },
  { id: 'kundan-3', url: '/Kundan/3.jpg', alt: 'Kundan jewelry 3', collection: 'kundan' },
  { id: 'kundan-4', url: '/Kundan/4.jpg', alt: 'Kundan jewelry 4', collection: 'kundan' },
  { id: 'kundan-5', url: '/Kundan/5.jpg', alt: 'Kundan jewelry 5', collection: 'kundan' },
  { id: 'kundan-6', url: '/Kundan/6.jpg', alt: 'Kundan jewelry 6', collection: 'kundan' },
  { id: 'kundan-7', url: '/Kundan/7.jpg', alt: 'Kundan jewelry 7', collection: 'kundan' },
  { id: 'kundan-8', url: '/Kundan/8.jpg', alt: 'Kundan jewelry 8', collection: 'kundan' },
  { id: 'kundan-9', url: '/Kundan/9.jpg', alt: 'Kundan jewelry 9', collection: 'kundan' },
  { id: 'kundan-10', url: '/Kundan/10.jpg', alt: 'Kundan jewelry 10', collection: 'kundan' },
  { id: 'kundan-11', url: '/Kundan/11.jpg', alt: 'Kundan jewelry 11', collection: 'kundan' },
  { id: 'kundan-12', url: '/Kundan/12.jpg', alt: 'Kundan jewelry 12', collection: 'kundan' },
];

export const CollectionsSection: React.FC<CollectionsSectionProps> = ({ onImageClick }) => {
  const [activeTab, setActiveTab] = useState<'gold' | 'silver' | 'kundan'>('gold');
  const [isHovered, setIsHovered] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  const SCROLL_SPEED = 0.5; // pixels per frame
  const CARD_WIDTH = 350;
  const CARD_GAP = 24; // gap between cards
  const CARD_TOTAL_WIDTH = CARD_WIDTH + CARD_GAP;

  // Filter images by active collection
  const activeImages = collectionData.filter(img => img.collection === activeTab);

  // Duplicate images for infinite scroll effect
  const duplicatedImages = [...activeImages, ...activeImages, ...activeImages];

  // Handle tab change
  const handleTabChange = (tab: 'gold' | 'silver' | 'kundan') => {
    setActiveTab(tab);
    setScrollPosition(0); // Reset scroll position when switching tabs
  };

  // Auto-scroll animation
  useEffect(() => {
    const animate = () => {
      if (!isHovered) {
        setScrollPosition((prev) => {
          const maxScroll = activeImages.length * CARD_TOTAL_WIDTH;
          const newPosition = prev + SCROLL_SPEED;
          
          // Reset to start when we've scrolled through one full set
          if (newPosition >= maxScroll) {
            return 0;
          }
          
          return newPosition;
        });
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isHovered, activeTab, activeImages.length, CARD_TOTAL_WIDTH]);

  // Handle mouse enter (pause auto-scroll)
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  // Handle mouse leave (resume auto-scroll)
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <section id="collections" className="py-20 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        {/* Tagline */}
        <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase text-center mb-4">
          Handcrafted Elegance
        </p>

        {/* Title */}
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-center text-white mb-12">
          Exquisite Collections
        </h2>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12 space-x-12">
          <button
            onClick={() => handleTabChange('gold')}
            className={`pb-2 font-semibold uppercase text-sm tracking-wider transition-all duration-300 relative ${
              activeTab === 'gold'
                ? 'text-yellow-500'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            Gold
            {activeTab === 'gold' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-500"></div>
            )}
          </button>
          <button
            onClick={() => handleTabChange('silver')}
            className={`pb-2 font-semibold uppercase text-sm tracking-wider transition-all duration-300 relative ${
              activeTab === 'silver'
                ? 'text-yellow-500'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            Silver
            {activeTab === 'silver' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-500"></div>
            )}
          </button>
          <button
            onClick={() => handleTabChange('kundan')}
            className={`pb-2 font-semibold uppercase text-sm tracking-wider transition-all duration-300 relative ${
              activeTab === 'kundan'
                ? 'text-yellow-500'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            Kundan
            {activeTab === 'kundan' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-500"></div>
            )}
          </button>
        </div>

        {/* Horizontal Scrolling Carousel */}
        <div className="relative overflow-hidden">
          {/* Scrolling Container */}
          <div
            ref={scrollContainerRef}
            className="flex"
            style={{
              transform: `translateX(-${scrollPosition}px)`,
              transition: 'none',
              gap: `${CARD_GAP}px`,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {duplicatedImages.map((image, index) => (
              <div
                key={`${image.id}-${index}`}
                className="flex-shrink-0 relative overflow-hidden rounded-3xl cursor-pointer group bg-gray-900 shadow-2xl"
                style={{ width: `${CARD_WIDTH}px`, height: '435px' }}
                onClick={() => onImageClick(image.url)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
              </div>
            ))}
          </div>

          {/* Pause Indicator - Centered */}
          {isHovered && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-2 z-10">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
              <span>Paused</span>
            </div>
          )}
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center mt-8 space-x-2">
          {activeImages.map((_, index) => {
            const currentSet = Math.floor(scrollPosition / CARD_TOTAL_WIDTH);
            const isActive = currentSet % activeImages.length === index;
            return (
              <div
                key={index}
                className={`transition-all duration-300 rounded-full ${
                  isActive
                    ? 'w-8 h-2 bg-yellow-500'
                    : 'w-2 h-2 bg-gray-600'
                }`}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};
